import { FetchResult, useMutation, useQuery } from '@apollo/client';
import { useCallback, useMemo } from 'react';

import { useRdContext } from '@neo.tax/rd/context';
import {
  RECORD_SUBMIT_DOCUMENTS,
  GET_ALL_TAX_DOCUMENTS,
  TaxAuthority,
  DocumentType,
  RecordSubmitDocumentsMutation,
  RecordSubmitDocumentsMutationVariables,
  GET_FISCAL_YEAR,
  GetFiscalYearQueryVariables,
  GetAllDocumentsQuery,
  GetAllDocumentsQueryVariables,
  useCurrentCompanyId,
  DocumentStatus,
} from '@neo.tax/shared';

export const useSubmitTaxDocuments = (): {
  submitTaxDocuments: (companyId: string) => Promise<FetchResult<RecordSubmitDocumentsMutation>>;
  disabled: boolean;
} => {
  const companyId = useCurrentCompanyId();
  const { fiscalYear } = useRdContext();

  const [recordSubmitDocumentsForReview] = useMutation<
    RecordSubmitDocumentsMutation,
    RecordSubmitDocumentsMutationVariables
  >(RECORD_SUBMIT_DOCUMENTS);

  const { data, loading } = useQuery<GetAllDocumentsQuery, GetAllDocumentsQueryVariables>(GET_ALL_TAX_DOCUMENTS, {
    variables: { companyId },
  });

  const disabled = useMemo(() => {
    const documentsForFiscalYear = data?.getAllDocuments.find((documents) => documents.id === fiscalYear.id);
    const federalRdTaxCreditDocuments = documentsForFiscalYear?.federalRdTaxCreditDocuments;
    if (!federalRdTaxCreditDocuments) {
      return false;
    }
    const { taxCreditFormStatus, taxWriteUpStatus } = federalRdTaxCreditDocuments;

    const canSubmit =
      taxCreditFormStatus !== DocumentStatus.MissingInformation &&
      taxWriteUpStatus !== DocumentStatus.MissingInformation &&
      !fiscalYear.lockedForCustomers;
    return !canSubmit;
  }, [data?.getAllDocuments, fiscalYear]);

  const submitTaxDocuments = useCallback(
    (companyId: string): Promise<FetchResult<RecordSubmitDocumentsMutation>> =>
      // submit federal tax documents
      recordSubmitDocumentsForReview({
        variables: {
          fiscalYearId: fiscalYear.id,
          documentInputs: [
            {
              documentType: DocumentType.RdCreditAndCapitalizationWithInstructions,
              taxAuthority: TaxAuthority.Federal,
            },
            {
              documentType: DocumentType.WriteUp,
              taxAuthority: TaxAuthority.Federal,
            },
          ],
        },
        refetchQueries: [
          {
            query: GET_ALL_TAX_DOCUMENTS,
            variables: { companyId } satisfies GetAllDocumentsQueryVariables,
          },
          {
            query: GET_FISCAL_YEAR,
            variables: { fiscalYearId: fiscalYear.id } satisfies GetFiscalYearQueryVariables,
          },
        ],
      }),
    [recordSubmitDocumentsForReview, fiscalYear],
  );

  return {
    submitTaxDocuments,
    disabled: loading || disabled,
  };
};
